import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getLegalDocument } from '../../legal';
import { LEGAL_DOC_VERSION } from '../../legal/versions';

/**
 * @param {{ doc: 'terms' | 'privacy' }} props
 */
export default function LegalDocumentPage({ doc }) {
  const { t, language } = useLanguage();
  const content = getLegalDocument(doc, language === 'en' ? 'en' : 'fr');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 p-4">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <Link
          to="/login"
          className="inline-flex items-center text-blue-600 mb-6 hover:text-blue-800"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('legal.back')}
        </Link>

        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{content.title}</h1>
          <p className="text-sm text-gray-500">
            {content.lastUpdatedLabel}: {content.lastUpdated}
            <span className="text-gray-400"> · v{LEGAL_DOC_VERSION}</span>
          </p>
        </header>

        <div className="space-y-8 text-gray-700 leading-relaxed">
          {content.sections.map((section, sectionIndex) => (
            <section key={`${sectionIndex}-${section.heading}`}>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">{section.heading}</h2>
              {(section.paragraphs || []).map((p, i) => (
                <p key={`p-${sectionIndex}-${i}`} className="mb-3">
                  {p}
                </p>
              ))}
              {section.list?.length ? (
                <ul className="list-disc pl-5 space-y-1.5 mb-3">
                  {section.list.map((item, i) => (
                    <li key={`li-${sectionIndex}-${i}`}>{item}</li>
                  ))}
                </ul>
              ) : null}
              {(section.paragraphsAfterList || []).map((p, i) => (
                <p key={`pa-${sectionIndex}-${i}`} className="mb-3">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </div>

        <footer className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap gap-4 text-sm">
          <Link to="/terms" className="text-blue-600 hover:underline">
            {t('legal.terms')}
          </Link>
          <Link to="/privacy" className="text-blue-600 hover:underline">
            {t('legal.privacy')}
          </Link>
          <Link to="/login" className="text-blue-600 hover:underline">
            {t('auth.login.link')}
          </Link>
        </footer>
      </div>
    </div>
  );
}
